/* eslint-disable no-console */
import {EyeInvisibleOutlined, EyeTwoTone} from '@ant-design/icons';
import {Form, Input, Button, Typography} from 'antd';
import React, {useState} from 'react';

import {verifyAndChangePassword} from '../../../services/modules/User/forgotPassword';
import {errorMessages} from '../../signUp/Constants/errorMessages';
import {IChangePasswordFormData} from '../Utils/Interfaces';

interface IFormData {
  password: string;
}

const {Title, Text} = Typography;

const ChangePasswordScreen: React.FC<IChangePasswordFormData> = ({
  email,
  otp,
  onFinish,
  onError
}) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [buttonAction, setButtonAction] = useState<boolean>();

  const handleFinish = async (values: IFormData) => {
    setButtonAction(true);
    await verifyAndChangePassword({
      email: email,
      otp: otp,
      password: values?.password
    })
      .then((data) => {
        setButtonAction(false);
        if (data?.status === 'error') {
          onError();
        } else if (data.status === 'success') {
          onFinish();
        }
      })
      .catch(() => {
        setButtonAction(false);
      });
  };

  const validateForm = () => {
    const hasErrors = form
      .getFieldsError()
      .some(({errors}) => errors.length > 0);
    const allTouched = form.isFieldsTouched(true);
    setIsFormValid(!hasErrors && allTouched);
  };

  return (
    <>
      <Title level={3} style={{textAlign: 'left', marginBottom: 8}}>
        Create New Password
      </Title>
      <Text
        type="secondary"
        style={{display: 'block', textAlign: 'left', marginBottom: 15}}
      >
        Enter a strong new password for your account.
      </Text>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          void handleFinish(values);
        }}
        onValuesChange={validateForm}
        onFieldsChange={validateForm} // to catch blur validations
      >
        <Form.Item
          name="password"
          label="New Password"
          rules={[
            {required: true, message: `${errorMessages.requiredPassword}`},
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
              message: `${errorMessages.invalidPassword}`
            }
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input.Password
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: `${errorMessages.requiredConfirmPassword}`
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
              message: `${errorMessages.invalidConfirmPassword}`
            },
            ({getFieldValue}) => ({
              async validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return;
                }
                await new Promise((resolve) => setTimeout(resolve, 0));
                throw new Error('Passwords do not match');
              }
            })
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input.Password
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            disabled={!isFormValid}
            style={{
              backgroundColor: '#159a84',
              borderRadius: 8,
              height: 44,
              fontWeight: 600
            }}
            loading={buttonAction}
          >
            {buttonAction ? 'In Progress' : 'Update Password'}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default ChangePasswordScreen;
